import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ChevronLeft, Check, Copy, CreditCard, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Gate } from "@/components/wallet/Gate";
import { useWallet } from "@/lib/wallet/store";
import { overviewQuery, priceQuery } from "@/lib/wallet/queries";
import { addressFingerprint, formatAmount } from "@/lib/wallet/format";

export const Route = createFileRoute("/fund")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Fund your account — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Activate your XRP Ledger account by funding it with XRP — buy with USD or transfer from an exchange straight to your self-custodied address.",
      },
      { property: "og:title", content: "Fund your account — Cobalt Wallet" },
      {
        property: "og:description",
        content: "Activate your XRPL account and start sending and swapping on mainnet.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <FundScreen />
    </Gate>
  ),
});

/** A comfortable starting balance: activation reserve + a little to actually spend. */
const SUGGESTED_BUFFER = 10;

function FundScreen() {
  const { address, settings } = useWallet();
  const overview = useQuery({ ...overviewQuery(address), refetchInterval: 15_000 });
  const price = useQuery(priceQuery(settings.fiatLookup));
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const data = overview.data;
  const reserve = data?.baseReserve ?? 1;
  const funded = data?.funded ?? false;
  const suggested = reserve + SUGGESTED_BUFFER;
  const usd = price.data ? suggested * price.data : null;

  useEffect(() => {
    let cancelled = false;
    if (!address) return;
    void (async () => {
      const QRCode = (await import("qrcode")).default;
      const url = await QRCode.toDataURL(address, {
        margin: 1,
        width: 512,
        color: { dark: "#e8e6e1", light: "#0b0d1000" },
      });
      if (!cancelled) setQr(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [address]);

  return (
    <div className="animate-rise space-y-4">
      <div className="flex items-center gap-2">
        <Link
          to="/"
          className="grid size-8 place-items-center rounded-lg border border-border text-muted"
        >
          <ChevronLeft className="size-4" />
        </Link>
        <h1 className="text-[18px] font-medium tracking-tight">Fund your account</h1>
      </div>

      <section
        className={`panel space-y-1 p-4 ${funded ? "border-success/40" : "border-accent/40"}`}
      >
        <p className="label-instrument">Status</p>
        <p className="readout text-[15px]">
          {overview.isLoading
            ? "checking the ledger…"
            : funded
              ? "Active on mainnet"
              : "Not activated yet"}
        </p>
        <p className="readout text-[10px] leading-relaxed text-faint">
          {funded
            ? `Balance ${formatAmount(data?.xrp ?? 0, true)} XRP · ${formatAmount(data?.spendable ?? 0, true)} XRP spendable after the reserve. You can send and swap now.`
            : `The XRP Ledger keeps every account activated with a ${formatAmount(reserve)} XRP reserve. Send at least that much here and the account opens on the next ledger — about four seconds.`}
        </p>
        <button
          type="button"
          onClick={() => void overview.refetch()}
          className="readout mt-2 flex items-center gap-1.5 text-[10px] text-muted transition-colors hover:text-foreground"
        >
          <RefreshCw className={`size-3 ${overview.isFetching ? "animate-spin" : ""}`} /> check again
        </button>
      </section>

      {!funded ? (
        <section className="panel p-4">
          <p className="label-instrument">Suggested first deposit</p>
          <p className="readout mt-1 text-[22px]">{formatAmount(suggested)} XRP</p>
          <p className="readout mt-1 text-[10px] text-faint">
            {usd ? `≈ $${usd.toFixed(2)} · ` : ""}
            {formatAmount(reserve)} XRP stays locked as the account reserve, the rest is yours to
            send and swap. A first real test payment costs well under a cent in network fees.
          </p>
        </section>
      ) : null}

      <section className="panel space-y-3 p-4">
        <p className="label-instrument">Option 1 — buy with USD</p>
        <p className="readout text-[10px] leading-relaxed text-faint">
          Pay by card or bank transfer and the XRP is delivered straight to the address below.
          Cobalt never touches the money or your card details.
        </p>
        <Link
          to="/buy"
          className="readout flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-accent text-[11px] uppercase tracking-[0.15em] text-accent-ink"
        >
          <CreditCard className="size-3.5" /> Buy XRP
        </Link>
      </section>

      <section className="panel space-y-3 p-4">
        <p className="label-instrument">Option 2 — transfer from an exchange</p>
        <p className="readout text-[10px] leading-relaxed text-faint">
          Withdraw XRP from Coinbase, Kraken, Uphold, Gemini or any wallet to the address below.
          Choose the XRP Ledger network and leave the destination tag blank — this account does not
          need one.
        </p>

        <div className="mx-auto grid size-44 place-items-center rounded-xl border border-border bg-background p-3">
          {qr ? (
            <img src={qr} alt="QR code for your XRPL address" className="size-full" />
          ) : (
            <p className="label-instrument">generating…</p>
          )}
        </div>

        <div className="rounded-lg border border-border bg-background p-3">
          <p className="readout break-all text-[12px] leading-relaxed">{address}</p>
          <p className="readout mt-1.5 flex items-center gap-1.5 text-[10px] text-success">
            <ShieldCheck className="size-3" /> Fingerprint {addressFingerprint(address ?? "")} —
            check the first and last characters match before you send.
          </p>
        </div>

        <button
          type="button"
          onClick={async () => {
            if (!address) return;
            await navigator.clipboard.writeText(address);
            setCopied(true);
            toast.success("Address copied");
          }}
          className="readout flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-border bg-background text-[11px] uppercase tracking-[0.15em]"
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />} Copy address
        </button>
      </section>

      <section className="panel space-y-2 p-4">
        <p className="label-instrument">Once it lands</p>
        <ol className="readout space-y-1.5 text-[10px] leading-relaxed text-faint">
          <li>1 · This screen flips to Active on its own, within a few seconds of arrival.</li>
          <li>
            2 · Try a small real payment first — send 1 XRP to an address you control and confirm it
            appears in Activity.
          </li>
          <li>
            3 · Then try a small swap on the DEX. Add the token's trustline first, which locks a
            further 0.2 XRP of reserve.
          </li>
        </ol>
        {funded ? (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Link
              to="/send"
              className="readout flex h-11 items-center justify-center rounded-lg bg-accent text-[11px] uppercase tracking-[0.15em] text-accent-ink"
            >
              Send
            </Link>
            <Link
              to="/swap"
              search={{ pay: undefined, receive: undefined, amount: undefined }}

              className="readout flex h-11 items-center justify-center rounded-lg border border-border bg-background text-[11px] uppercase tracking-[0.15em]"
            >
              DEX Swap
            </Link>
          </div>
        ) : null}
      </section>
    </div>
  );
}
